/**
 * useBodyScrollLock - Prevents body scroll when modal/overlay is open
 * Handles iOS Safari quirks with position:fixed approach
 */

import { useEffect, useRef } from 'react';

export const useBodyScrollLock = (isLocked) => {
  const scrollYRef = useRef(0);

  useEffect(() => {
    if (!isLocked) return;

    const body = document.body;
    const html = document.documentElement;

    // Store current scroll position
    scrollYRef.current = window.scrollY;

    // Get scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    // Apply styles to prevent scrolling
    // Using position:fixed + top offset is the most reliable iOS Safari fix
    const originalStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollYRef.current}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    // Compensate for scrollbar width to prevent layout shift
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      // Restore original styles
      body.style.position = originalStyles.position;
      body.style.top = originalStyles.top;
      body.style.left = originalStyles.left;
      body.style.right = originalStyles.right;
      body.style.width = originalStyles.width;
      body.style.overflow = originalStyles.overflow;
      body.style.paddingRight = originalStyles.paddingRight;

      // Restore scroll position
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isLocked]);
};

export default useBodyScrollLock;
